/**
 * AdminWorkReportDetailPage — the read-only workspace for one report
 * (D2f), at ``/admin/work-reports/:workReportId``.
 *
 * Before this page a report could only be opened as a form. That made
 * reading one — what was done, how long it took, what it hangs off —
 * an act of editing. Now the row opens the report and a separate button
 * opens the form.
 *
 * Layout (REPORT-UX1): a wide header card, then a MAIN column of what
 * people read and discuss — Beschreibung, Rapportdetails,
 * Verknüpfungen, Notizen, Aktivität — beside a narrow sidebar of what
 * they classify and attach — Aufwand & Verlauf, Tätigkeitsbereich,
 * Anhänge. Single stack below ``lg``, sidebar last.
 *
 * SAFE-DELETE1: there is no delete. The header offers "Rapport
 * stornieren" (mandatory reason via the shared dialog); a cancelled
 * report shows a banner + badge, loses its edit/cancel actions, and its
 * notes and attachments turn read-only — mirroring the backend's 409
 * freeze, which is the actual enforcement.
 *
 * Editing reuses the ONE existing ``WorkReportFormModal``. There is no
 * second form and no edit route: on save the modal closes, a toast
 * fires and the page refetches in place, so the URL never changes.
 *
 * The page is an orchestrator (CLAUDE.md Regel 48) — it fetches, holds
 * modal state and composes. Every card lives in ``workReports/`` and
 * every display rule in ``workReportDetailHelpers``.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { fetchAdminWorkReportById } from "../../api/adminData";
import { useAuth } from "../../contexts/AuthContext";
import WorkReportCancelDialog from "./calendar/workReports/WorkReportCancelDialog";
import WorkReportFormModal from "./calendar/workReports/WorkReportFormModal";
import EntityActivityFeed from "./activity/EntityActivityFeed";
import WorkReportDetailHeader from "./workReports/WorkReportDetailHeader";
import {
  WorkReportDescriptionCard,
  WorkReportEffortCard,
  WorkReportFactsCard,
  WorkReportLinksCard,
} from "./workReports/WorkReportDetailCards";
import {
  WorkReportAttachmentsSection,
  WorkReportCancelledBanner,
  WorkReportCategoryCard,
  WorkReportCommentsSection,
} from "./workReports/WorkReportWorkspaceCards";
import { reportTitle } from "./workReports/workReportDetailHelpers";

/** The reports overview is the calendar page in report mode — there is
 * no separate /admin/work-reports list, so the breadcrumb points where
 * the user actually came from. */
export const REPORTS_OVERVIEW_PATH = "/admin/calendar?mode=reports";

const SKELETON_CARD =
  "rounded-[12px] border border-border bg-card p-5 shadow-sm " +
  "dark:border-[#1e2130] dark:bg-[#13161f]";
const SKELETON_BAR = "animate-pulse rounded bg-muted dark:bg-[#1e2130]";

function Breadcrumb({ current }) {
  return (
    <nav
      aria-label="Breadcrumb"
      className="mb-4 text-[11px] text-muted-foreground dark:text-[#4a5070]"
    >
      <span>Betrieb</span>
      <span className="mx-2" aria-hidden="true">
        /
      </span>
      <Link
        to={REPORTS_OVERVIEW_PATH}
        className="rounded-[4px] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:hover:text-[#94a3b8]"
      >
        Rapporte
      </Link>
      <span className="mx-2" aria-hidden="true">
        /
      </span>
      <span aria-current="page" className="text-foreground dark:text-[#94a3b8]">
        {current}
      </span>
    </nav>
  );
}

/** Skeletons in the shape the page actually settles into — wide header,
 * then the two columns — so nothing jumps when the data lands. */
function DetailSkeleton() {
  return (
    <div aria-hidden="true">
      <div className={`${SKELETON_CARD} mb-4 flex items-center gap-4`}>
        <div className={`${SKELETON_BAR} h-14 w-14 rounded-full`} />
        <div className="flex-1">
          <div className={`${SKELETON_BAR} h-6 w-2/5`} />
          <div className={`${SKELETON_BAR} mt-2 h-4 w-20`} />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="space-y-4">
          <div className={`${SKELETON_CARD} h-[150px]`} />
          <div className={`${SKELETON_CARD} h-[300px]`} />
        </div>
        <div className="space-y-4">
          <div className={`${SKELETON_CARD} h-[110px]`} />
          <div className={`${SKELETON_CARD} h-[240px]`} />
        </div>
      </div>
    </div>
  );
}

export default function AdminWorkReportDetailPage() {
  const { workReportId } = useParams();
  const { user: currentUser } = useAuth();
  const currentUserId = currentUser?.id || null;

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  // SAFE-DELETE1: the report pending cancellation (the shared dialog's
  // contract), or null while closed.
  const [pendingCancel, setPendingCancel] = useState(null);
  // Bumped by notes/attachments mutations so the activity feed refetches
  // without a page reload.
  const [activityRefreshKey, setActivityRefreshKey] = useState(0);

  /**
   * Loads the report itself. D3: the activity timeline is no longer
   * fetched here — EntityActivityFeed owns its own request, so a broken
   * audit feed can no longer take the report down with it.
   */
  const load = useCallback(async () => {
    if (!workReportId) return;
    setLoading(true);
    setError(null);
    setNotFound(false);
    try {
      const data = await fetchAdminWorkReportById(workReportId);
      setReport(data);
    } catch (err) {
      // 404 covers both "no such report" and "belongs to another
      // organisation" — the backend answers identically for the two, and
      // so does this page.
      if (err?.status === 404) {
        setNotFound(true);
      } else {
        setError(err?.message || "Der Rapport konnte nicht geladen werden.");
      }
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [workReportId]);

  useEffect(() => {
    void load();
  }, [load]);

  // The employee dropdown of the edit modal. Independent of the report:
  // failing to load users must not blank the page.
  const handleSaved = useCallback(() => {
    void load();
  }, [load]);

  const handleChildChanged = useCallback(() => {
    setActivityRefreshKey((k) => k + 1);
  }, []);

  const isCancelled = Boolean(report?.is_cancelled);

  const title = report ? reportTitle(report) : "…";

  if (loading) {
    return (
      <div className="mx-auto max-w-[1400px]">
        <Breadcrumb current="Rapportdetails" />
        <p role="status" className="sr-only">
          Rapport wird geladen …
        </p>
        <DetailSkeleton />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="mx-auto max-w-[1400px]">
        <Breadcrumb current="Rapportdetails" />
        <div
          role="status"
          className="rounded-[12px] border border-dashed border-border bg-card p-8 text-center dark:border-[#1e2130] dark:bg-[#13161f]"
        >
          <p className="m-0 text-[14px] font-medium text-foreground dark:text-[#e2e4ed]">
            Der Rapport wurde nicht gefunden oder du hast keinen Zugriff darauf.
          </p>
          <Link
            to={REPORTS_OVERVIEW_PATH}
            className="mt-4 inline-flex rounded-[8px] border border-border bg-transparent px-3 py-1.5 text-[12px] font-semibold text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:border-[#1e2130] dark:text-[#c5cbe0]"
          >
            Zurück zur Rapportübersicht
          </Link>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-[1400px]">
        <Breadcrumb current="Rapportdetails" />
        <div
          role="alert"
          className="rounded-[12px] border border-destructive/40 bg-destructive/10 p-6 dark:border-[#3a1f24] dark:bg-[#1e0d10]"
        >
          <p className="m-0 text-[13px] font-medium text-destructive dark:text-[#ff6b6b]">
            {error}
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={load}
              className="rounded-[8px] border border-destructive/40 bg-transparent px-3 py-1.5 text-[12px] font-semibold text-destructive hover:bg-destructive/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Erneut versuchen
            </button>
            <Link
              to={REPORTS_OVERVIEW_PATH}
              className="rounded-[8px] border border-border bg-transparent px-3 py-1.5 text-[12px] font-semibold text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:border-[#1e2130] dark:text-[#c5cbe0]"
            >
              Zurück zur Rapportübersicht
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1400px]">
      <Breadcrumb current="Rapportdetails" />

      <WorkReportDetailHeader
        title={title}
        isCancelled={isCancelled}
        isJointCapture={Boolean(report?.creation_batch_id)}
        onEditClick={() => setModalOpen(true)}
        onCancelClick={() => setPendingCancel(report)}
      />

      <WorkReportCancelledBanner report={report} />

      {/* Main (reading + discussion) beside a narrow sidebar
          (classification + files); stacked below lg, sidebar last —
          the same split the task workspace uses. */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_340px] xl:grid-cols-[minmax(0,1fr)_360px]">
        <div className="min-w-0 space-y-4">
          <WorkReportDescriptionCard report={report} />
          <WorkReportFactsCard report={report} />
          <WorkReportLinksCard report={report} />
          {/* REPORT-NOTES1 — what people say about the report after the
              fact; the description above is what was done, the feed
              below is what the system recorded. */}
          <WorkReportCommentsSection
            reportId={workReportId}
            currentUserId={currentUserId}
            onChanged={handleChildChanged}
            readOnly={isCancelled}
          />
          {/* D3: same source (the report's own rows + audit), rendered
              by the one shared feed component. refreshKey lets a note
              or attachment mutation surface without a reload. */}
          <EntityActivityFeed
            entityType="work_report"
            entityId={workReportId}
            headingId="report-activity-heading"
            refreshKey={activityRefreshKey}
          />
        </div>
        <div className="min-w-0 space-y-4">
          <WorkReportEffortCard report={report} />
          <WorkReportCategoryCard code={report?.activity_category} />
          <WorkReportAttachmentsSection
            reportId={workReportId}
            onChanged={handleChildChanged}
            readOnly={isCancelled}
          />
        </div>
      </div>

      <WorkReportFormModal
        open={modalOpen}
        mode="edit"
        report={report}
        currentUserId={currentUserId}
        onOpenChange={setModalOpen}
        onSaved={handleSaved}
      />

      {/* SAFE-DELETE1 — the ONE shared cancellation dialog. */}
      <WorkReportCancelDialog
        report={pendingCancel}
        onCancel={() => setPendingCancel(null)}
        onCancelled={() => {
          setPendingCancel(null);
          void load();
        }}
      />
    </div>
  );
}
